/**
 * Data loading for the blog index.
 *
 * The index cannot be a list in this repository, because a list in this
 * repository would mean a deployment per article. So it is discovered instead:
 * ask the asset store what exists, then read what it says.
 *
 * Two requests, not one per article:
 *
 *   1. `sitemapData.queryAssets` enumerates `content/pages/`. This is the only
 *      Sharetribe endpoint that lists rather than fetches — the public asset
 *      API requires you to already know the path, which is exactly what a
 *      discovery step cannot know.
 *   2. `assetsByAlias` reads every discovered article in a single call, which
 *      is what that endpoint is for.
 *
 * Drafts never appear because Console publishes to the asset store on publish;
 * an unpublished page is not an asset. Exclusion is therefore structural rather
 * than a flag this code has to honour, and unpublishing removes an article for
 * the same reason.
 */

import { createSlice } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { denormalizeAssetData } from '../../util/data';
import {
  PAGE_ASSET_PREFIX,
  assetPathForSlug,
  articleSummaryFromAsset,
  blogSlugsFromAssets,
  pageIdFromAssetPath,
  slugFromPageId,
  sortArticles,
} from '../../util/blogPages';

const initialState = {
  articles: [],
  queryInProgress: false,
  queryError: null,
};

const blogPageSlice = createSlice({
  name: 'BlogPage',
  initialState,
  reducers: {
    queryRequest: state => {
      state.queryInProgress = true;
      state.queryError = null;
    },
    querySuccess: (state, action) => {
      state.queryInProgress = false;
      state.articles = action.payload;
    },
    queryError: (state, action) => {
      state.queryInProgress = false;
      state.queryError = action.payload;
    },
  },
});

export const { queryRequest, querySuccess, queryError } = blogPageSlice.actions;
export default blogPageSlice.reducer;

/**
 * Pairs each returned asset with the slug it belongs to.
 *
 * The multi-asset response is a JSON:API collection: each entry carries its
 * path and its page content under `attributes`, not at the top level. Reading
 * the wrong level does not throw — it yields `undefined` and an empty blog, so
 * the shape asserted in the tests is taken from the SDK's own fixtures rather
 * than from what this function would find convenient.
 *
 * Order is not guaranteed to match the request, so the asset's own path is what
 * identifies it rather than its index. An entry whose path cannot be read is
 * skipped: one unreadable page must not empty the index.
 */
const summariesFromResponse = response => {
  const denormalised = denormalizeAssetData(response?.data);
  const entries = Array.isArray(denormalised) ? denormalised : [];

  return entries.reduce((acc, entry) => {
    const { assetPath, data } = entry?.attributes || {};
    const pageId = pageIdFromAssetPath(assetPath);
    const slug = pageId ? slugFromPageId(pageId) : null;
    if (!slug || !data) {
      return acc;
    }
    return [...acc, articleSummaryFromAsset(data, slug)];
  }, []);
};

/**
 * Loads every published article's summary.
 *
 * An empty blog is a success, not an error: a marketplace that has published
 * nothing yet should get an empty index rather than a failure page.
 */
export const loadData = () => (dispatch, getState, sdk) => {
  dispatch(queryRequest());

  return sdk.sitemapData
    .queryAssets({ pathPrefix: `/${PAGE_ASSET_PREFIX}` })
    .then(response => {
      const slugs = blogSlugsFromAssets(response?.data?.data);

      if (slugs.length === 0) {
        dispatch(querySuccess([]));
        return [];
      }

      return sdk
        .assetsByAlias({ paths: slugs.map(assetPathForSlug), alias: 'latest' })
        .then(assetsResponse => {
          const articles = sortArticles(summariesFromResponse(assetsResponse));
          dispatch(querySuccess(articles));
          return articles;
        });
    })
    .catch(e => {
      dispatch(queryError(storableError(e)));
      // Resolved rather than rejected: the index renders its own empty state,
      // and a rejected loadData would render a server error page instead.
      return [];
    });
};
